import { useEffect, useRef, useState } from 'react';

import { useAuth, useClerk, useSignIn } from '@clerk/clerk-react';
import { httpService, IconWrapper, getPostAuthRedirectUrl, triggerKeychainSave } from '@flexo/general';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  doPasswordsMatch,
  isPasswordValid,
  PasswordConditions,
  Textfield,
} from '@flexo/atoms';
import { useFormValidation } from '@flexo/hooks';
import { ALERT } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import queryString from 'query-string';
import styles from './register-by-invite.module.scss';

const ENV = (import.meta as any).env;
const loader = '/loaderLottie.json';

export function RegisterByInvite() {
  const formRef = useRef<any>(null);
  const passwordRef = useRef<any>(null);
  const confirmPasswordRef = useRef<any>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { signOut, isSignedIn, isLoaded } = useAuth();
  const { signIn, setActive } = useSignIn();
  const clerk = useClerk();
  const [passwordresetAsked, setPasswordResetAsked] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [askError, setAskError] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [paramsData, setParamsData] = useState({
    email: '',
  });

  const { title, subtitle, inputs, buttonLabel } = t(
    'pages.register_by_invite',
    { returnObjects: true, email: paramsData.email }
  ) as any;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    password_confirm: '',
  });

  // Form validation hook
  const {
    passwordBlur,
    passwordValidation,
    handleBlur,
    handleFocus,
    handlePasswordValidation,
  } = useFormValidation(
    {
      fields: ['password', 'password_confirm'],
      hasPasswordFields: true,
    },
    formData
  );

  const [toggleIcons, setToggleIcons] = useState({
    password: 'hide',
    password_confirm: 'hide',
  });

  const [toggleTypes, setToggleTypes] = useState({
    password: 'password',
    password_confirm: 'password',
  });

  async function handleInputChange(event: any) {
    const { name, value } = event.target;

    setAskError(false);

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'password') {
      handlePasswordValidation(value, formData.password_confirm);
    }
    if (name === 'password_confirm') {
      handlePasswordValidation(formData.password, value);
    }
  }

  function togglePasswordVisibility(inputName: string) {
    setToggleIcons({
      ...toggleIcons,
      [inputName]: toggleIcons[inputName] === 'hide' ? 'show' : 'hide',
    });

    setToggleTypes({
      ...toggleTypes,
      [inputName]: toggleTypes[inputName] === 'password' ? 'text' : 'password',
    });
  }

  async function askForRegistration() {
    setPasswordResetAsked(true);

    // v1_4 endpoint - same path and request format
    const response = await httpService
      .post({
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/users/complete-registration`,
        data: {
          email: paramsData.email,
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          organizationId: ENV.VITE_APP_ORG_ID,
        },
      })
      .then((resp: any) => ({
        status: resp.status,
        statusText: resp?.statusText || 'Unknown status text',
        message: resp?.data?.message || 'Unknown message',
      }))
      .catch((error) => {
        // console.warn(error);
        return {
          status: error?.response?.status || 500,
          statusText: error?.response?.statusText || 'Unknown error',
        };
      });

    setPasswordResetAsked(false);

    if (response.status > 200) {
      if (response.status === 422) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );
      } else if (response.status === 403) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_NOT_ALLOWED',
              },
            },
          } as any)
        );
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );
      }

      setAskError(true);
    } else {
      setAskError(false);
      setRegistrationSuccess(true);

      // Offer the new credentials to the password manager / iOS Keychain now,
      // while the fields are still in the DOM and the user's interaction is fresh.
      // Deferred to after auto-login would mean navigating away before the prompt.
      triggerKeychainSave(paramsData.email, formData.password);

      // Auto-login with the new credentials
      // Note: User was already signed out when landing on this page (see useEffect)
      try {
        if (!signIn || !setActive) {
          throw new Error('Clerk signIn not available');
        }

        // Wait for Clerk to propagate the newly created user
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Retry logic with increasing delays to handle Clerk propagation
        const maxRetries = 3;
        let loginResult: any = null;
        let lastError: any = null;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            loginResult = await signIn.create({
              identifier: paramsData.email,
              password: formData.password,
            });

            if (loginResult.status === 'complete') {
              break; // Success, exit retry loop
            } else {
              throw new Error(`Auto-login status: ${loginResult.status}`);
            }
          } catch (err) {
            lastError = err;
            if (attempt < maxRetries) {
              // Wait longer between each retry (1s, 2s, 3s)
              await new Promise((resolve) =>
                setTimeout(resolve, 1000 * attempt)
              );
            }
          }
        }

        if (!loginResult || loginResult.status !== 'complete') {
          throw lastError || new Error('Auto-login did not complete after retries');
        }

        // Activate the session
        await setActive({ session: loginResult.createdSessionId });

        // Wait for Clerk to update client state
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Get organization membership and set active org via useClerk()
        await new Promise((resolve) => setTimeout(resolve, 300));
        const activeUser = clerk.user;
        const orgMemberships = activeUser?.organizationMemberships || [];
        const targetOrgId = ENV.VITE_APP_CLERK_ORG_ID;
        const orgMembership = targetOrgId
          ? orgMemberships.find((m: any) => m.organization.id === targetOrgId) || orgMemberships[0]
          : orgMemberships[0];

        if (orgMembership?.organization) {
          await clerk.setActive({ organization: orgMembership.organization.id });

          // Verify session is no longer pending
          await new Promise((resolve) => setTimeout(resolve, 300));
          const updatedStatus = clerk.session?.status;
          if (updatedStatus === 'pending') {
            try { await setActive({ session: null }); } catch (_) { /* noop */ }
            throw new Error('Session stuck in pending state');
          }
        }

        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_REGISTRATION_SUCCESS',
              },
            },
          } as any)
        );

        // Mark that auto-login just completed - prevents re-signout on remount
        sessionStorage.setItem('registrationAutoLoginComplete', 'true');

        // Navigate to home - user is now logged in
        setTimeout(() => {
          dispatch(ALERT.ResetAlert({} as any));
          const postAuthRedirectUrl = getPostAuthRedirectUrl();
          if (postAuthRedirectUrl) {
            window.location.href = postAuthRedirectUrl;
          } else {
            navigate(`/${ENV.VITE_APP_FIRST_PAGE}`);
          }
        }, 2000);
      } catch (autoLoginError) {

        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_REGISTRATION_SUCCESS',
              },
            },
          } as any)
        );

        // Fall back to login page if auto-login fails
        setTimeout(() => {
          dispatch(ALERT.ResetAlert({} as any));
          navigate('/login');
        }, 4000);
      }
    }
  }

  useEffect(() => {
    // Wait for Clerk to be loaded before checking auth state
    if (!isLoaded) return;
    if (hasInitialized) return;

    // Sign out any existing session IMMEDIATELY when landing on register page
    // This ensures a clean slate for new registration
    // BUT: if this is a remount after successful auto-login, redirect instead
    if (isSignedIn) {
      const autoLoginComplete = sessionStorage.getItem('registrationAutoLoginComplete');
      if (autoLoginComplete) {
        sessionStorage.removeItem('registrationAutoLoginComplete');
        navigate(`/${ENV.VITE_APP_FIRST_PAGE}`);
        return;
      }

      signOut(() => {
          // Callback syntax: Clerk clears session but does NOT navigate/reload
          setTimeout(() => {
            initializeRegistration();
          }, 300);
        }).catch((error) => {
          // Proceed anyway even if signout fails
          initializeRegistration();
        });
    } else {
      // Not signed in, proceed immediately
      initializeRegistration();
    }

    setHasInitialized(true);

    function initializeRegistration() {
      const params: any = queryString.parse(
        window.location.search.replace(/\+/g, '%2B')
      );
      const keywords = ['email'];

      // eslint-disable-next-line
      if (
        keywords.every((key: string) =>
          Object.prototype.hasOwnProperty.call(params, key)
        )
      ) {
        // Validate invitation before showing the form
        validateInvitation(params.email);
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_WRONG_PARAMS',
              },
            },
          } as any)
        );

        setTimeout(() => {
          dispatch(ALERT.ResetAlert({} as any));

          navigate('/login');
        }, 6000);
      }
    }

    async function validateInvitation(email: string) {
      // Set email immediately so the form can display it
      setParamsData({ email });

      try {
        // v1_4 endpoint - same path and response format
        const response = await httpService
          .get({
            url: `${
              ENV.VITE_APP_BASE_URL
            }/community/catalog/v1_4/organization/users/verify-invitation?email=${encodeURIComponent(
              email
            )}&organization_id=${ENV.VITE_APP_ORG_ID}`,
          })
          .then((resp: any) => ({
            status: resp.status,
            data: resp.data,
          }))
          .catch((err) => ({
            status: err?.response?.status || 500,
            data: err?.response?.data || {},
          }));

        if (response.status === 200 && response.data?.valid) {
          // Invitation is valid - form is already displayed
          // Logout will happen after successful registration
        } else if (response.status === 200 && response.data?.valid === false) {
          // Backend returned a specific validation failure (not HTTP error)
          // This happens for multi-org users who are already active
          const errorCode = response.data?.error;

          if (
            errorCode === 'ALREADY_ACTIVE' &&
            response.data?.redirectToLogin
          ) {
            // User already has an active account in another org
            // Save to sessionStorage and redirect immediately
            // The alert will be shown on the login page
            sessionStorage.setItem('showUserAlreadyActive', 'true');
            if (response.data?.message) {
              sessionStorage.setItem('userAlreadyActiveMessage', response.data.message);
            }
            navigate('/login');
          } else {
            // Other validation failures - generic error
            dispatch(
              ALERT.SetAlert({
                payload: {
                  value: {
                    type: 'BANNER_UNEXPECTED_ERROR',
                  },
                },
              } as any)
            );

            setTimeout(() => {
              dispatch(ALERT.ResetAlert({} as any));
              navigate('/login');
            }, 6000);
          }
        } else {
          // Extract error code from response
          // Backend can return code in: response.data.code, response.data.error,
          // or in detail field as "message. [CODE]"
          let errorCode = response.data?.code || response.data?.error;

          // Extract code from detail field if present (format: "message. [CODE]")
          if (!errorCode && response.data?.detail) {
            const match = response.data.detail.match(/\[([A-Z_]+)\]$/);
            if (match) {
              errorCode = match[1];
            }
          }

          // Map error codes to banner types
          let bannerType = 'BANNER_UNEXPECTED_ERROR';

          if (errorCode === 'INVITE_EXPIRED') {
            bannerType = 'BANNER_INVITE_EXPIRED';
          } else if (errorCode === 'ALREADY_ACTIVATED') {
            bannerType = 'BANNER_INVITE_ALREADY_ACTIVATED';
          } else if (errorCode === 'NOT_FOUND' || response.status === 404) {
            bannerType = 'BANNER_INVITE_NOT_FOUND';
          }

          // For ALREADY_ACTIVATED, save to sessionStorage and redirect immediately
          // The alert will be shown on the login page
          if (errorCode === 'ALREADY_ACTIVATED') {
            sessionStorage.setItem('showInviteAlreadyActivated', 'true');
            navigate('/login');
          } else {
            // For other errors, show alert before redirect
            dispatch(
              ALERT.SetAlert({
                payload: {
                  value: {
                    type: bannerType,
                  },
                },
              } as any)
            );

            setTimeout(() => {
              dispatch(ALERT.ResetAlert({} as any));
              navigate('/login');
            }, 6000);
          }
        }
      } catch {
        // Network or unexpected error
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );

        setTimeout(() => {
          dispatch(ALERT.ResetAlert({} as any));
          navigate('/login');
        }, 6000);
      }
    }
  }, [hasInitialized, dispatch, navigate, isLoaded]);

  return (
    <div className={styles.RegisterByInvite}>
      <div className={styles.RegisterByInvite__Header}>
        <IconWrapper
          iconName={'logo_wide'}
          className={styles.RegisterByInvite__Header__Logo}
        />
        <div className={styles.RegisterByInvite__Header__Title}>{title}</div>
        <div className={styles.RegisterByInvite__Header__Email}>
          {paramsData.email}
        </div>
      </div>

      <form
        id="registration_web_form"
        className={styles.RegisterByInvite__Form}
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          if (
            !registrationSuccess &&
            formData.firstName.trim() &&
            formData.lastName.trim() &&
            isPasswordValid(formData.password) &&
            doPasswordsMatch(formData.password, formData.password_confirm)
          ) {
            askForRegistration();
          }
        }}
      >
        {/* Hidden email field for keychain/password manager capture */}
        <input
          type="email"
          name="email"
          autoComplete="username"
          value={paramsData.email}
          readOnly
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
          tabIndex={-1}
        />

        {/* Name row - firstName and lastName side by side */}
        <div className={styles.RegisterByInvite__Form__NameRow}>
          <div className={styles.RegisterByInvite__Form__Input}>
            <Textfield
              textfieldColor={'grey'}
              variant="default"
              label={inputs.first_name.label}
              name="firstName"
              type="text"
              placeholder={inputs.first_name.placeholder}
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={handleInputChange}
            />
          </div>
          <div className={styles.RegisterByInvite__Form__Input}>
            <Textfield
              textfieldColor={'grey'}
              variant="default"
              label={inputs.last_name.label}
              name="lastName"
              type="text"
              placeholder={inputs.last_name.placeholder}
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck="false"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* password */}
        <div className={styles.RegisterByInvite__Form__Input}>
          <Textfield
            textfieldColor={'grey'}
            variant="default"
            ref={passwordRef}
            label={inputs.password.label}
            name="password"
            type={toggleTypes[inputs.password.name]}
            placeholder={inputs.password.placeholder}
            onPaste={(e) => false}
            onCopy={(e) => false}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            autoComplete="new-password"
            value={formData[inputs.password.name]}
            onChange={(ev: any) => handleInputChange(ev)}
            onBlur={handleBlur}
            onFocus={handleFocus}
            icon={toggleIcons[inputs.password.name]}
            onIconClick={() => togglePasswordVisibility(inputs.password.name)}
          />
        </div>

        {/* password conditions */}

        <PasswordConditions
          validation={passwordValidation}
          isFocused={!passwordBlur}
          conditionClassName={styles.RegisterByInvite__Conditions}
        />

        {/* password_confirm */}

        <div className={styles.RegisterByInvite__Form__Input}>
          <Textfield
            textfieldColor={'grey'}
            variant="default"
            ref={confirmPasswordRef}
            label={inputs.password_confirm.label}
            name={inputs.password_confirm.name}
            type={toggleTypes[inputs.password_confirm.name]}
            placeholder={inputs.password_confirm.placeholder}
            onPaste={(e) => false}
            onCopy={(e) => false}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            value={formData[inputs.password_confirm.name]}
            onChange={handleInputChange}
            autoComplete="off"
            icon={toggleIcons[inputs.password_confirm.name]}
            onIconClick={() =>
              togglePasswordVisibility(inputs.password_confirm.name)
            }
          />
        </div>
      </form>

      <div className={styles.RegisterByInvite__Footer}>
        <Button
          className={styles.RegisterByInvite__Footer__Button}
          label={
            passwordresetAsked ? (
              <Player
                autoplay
                loop
                src={loader}
                style={{ height: 20, width: 60 }}
              />
            ) : (
              buttonLabel
            )
          }
          variant="primary"
          color="secondary"
          disabled={
            registrationSuccess ||
            !formData.firstName.trim() ||
            !formData.lastName.trim() ||
            !isPasswordValid(formData.password) ||
            !doPasswordsMatch(formData.password, formData.password_confirm)
          }
          onClick={() => askForRegistration()}
        />
      </div>
    </div>
  );
}
